import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import { Controller, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { FlowStep, ToolCallStep } from "../../../../types";
import { TOOL_REGISTRY } from "../../toolRegistry";

const TOOLS = [
  "getAgeFromDateOfBirth",
  "isSalaryBackedLoanLocked",
  "setAsNotInterested",
  "requestCall",
  "upsertLead",
] as const;

const schema = z.object({
  tool_name: z.string().min(1, "Seleziona un tool"),
  output_field: z.string().min(1, "Campo obbligatorio"),
  next_step_id: z.string().optional(),
  order: z.number().int().min(0),
});

type FormData = z.infer<typeof schema>;

interface KVPair {
  key: string;
  value: string;
}

interface Props {
  step: ToolCallStep;
  steps: FlowStep[];
  onSave: (updated: ToolCallStep) => void;
}

export const ToolCallStepForm = ({ step, steps, onSave }: Props) => {
  const [inputMap, setInputMap] = useState<KVPair[]>(
    Object.entries(step.input_map).map(([key, value]) => ({ key, value }))
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      tool_name: step.tool_name,
      output_field: step.output_field,
      next_step_id: step.next_step_id ?? "",
      order: step.order,
    },
  });

  const toolName = useWatch({ control, name: "tool_name" });
  const lastResetToolName = useRef(step.tool_name);

  useEffect(() => {
    lastResetToolName.current = step.tool_name;
    reset({
      tool_name: step.tool_name,
      output_field: step.output_field,
      next_step_id: step.next_step_id ?? "",
      order: step.order,
    });
    setInputMap(Object.entries(step.input_map).map(([key, value]) => ({ key, value })));
  }, [step.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // When tool changes, reset inputMap with predefined param keys (empty values)
  useEffect(() => {
    if (!toolName || toolName === lastResetToolName.current) return;
    const toolDef = TOOL_REGISTRY[toolName];
    if (!toolDef || toolDef.params.length === 0) return;
    setInputMap(toolDef.params.map((p) => ({ key: p.key, value: "" })));
  }, [toolName]); // eslint-disable-line react-hooks/exhaustive-deps

  const updatePair = (idx: number, field: "key" | "value", val: string) => {
    setInputMap((prev) => prev.map((p, i) => (i === idx ? { ...p, [field]: val } : p)));
  };

  const addPair = () => setInputMap((prev) => [...prev, { key: "", value: "" }]);
  const removePair = (idx: number) => setInputMap((prev) => prev.filter((_, i) => i !== idx));

  const toolDef = toolName ? TOOL_REGISTRY[toolName] : undefined;
  const hasPredefinedParams = toolDef && toolDef.params.length > 0;
  // Index of predefined params so we know which rows are fixed
  const predefinedKeys = new Set(toolDef?.params.map((p) => p.key) ?? []);

  const onSubmit = handleSubmit((data) => {
    const map: Record<string, string> = {};
    inputMap.forEach(({ key, value }) => {
      if (key.trim()) map[key.trim()] = value;
    });
    onSave({
      ...step,
      tool_name: data.tool_name,
      input_map: map,
      output_field: data.output_field,
      next_step_id: data.next_step_id || undefined,
      order: data.order,
    });
  });

  return (
    <form onSubmit={onSubmit} className="flex-col gap-2">
      <Controller
        name="tool_name"
        control={control}
        render={({ field }) => (
          <FormControl fullWidth size="small" error={!!errors.tool_name}>
            <InputLabel>Tool</InputLabel>
            <Select {...field} label="Tool" value={field.value ?? ""}>
              {TOOLS.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </Select>
            {errors.tool_name && (
              <FormHelperText>{errors.tool_name.message}</FormHelperText>
            )}
          </FormControl>
        )}
      />

      <Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography variant="body2">Input map</Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={addPair}>
            Aggiungi
          </Button>
        </Box>
        {inputMap.map((pair, idx) => {
          const isPredefined = predefinedKeys.has(pair.key);
          const paramDef = toolDef?.params.find((p) => p.key === pair.key);
          return (
            <Box key={idx} sx={{ display: "flex", gap: 1, mb: 1, alignItems: "flex-start" }}>
              <TextField
                size="small"
                label="Chiave"
                value={pair.key}
                onChange={(e) => updatePair(idx, "key", e.target.value)}
                disabled={isPredefined}
                sx={{ flex: 1 }}
              />
              <TextField
                size="small"
                label="Valore"
                value={pair.value}
                onChange={(e) => updatePair(idx, "value", e.target.value)}
                helperText={isPredefined ? paramDef?.description : undefined}
                sx={{ flex: 1 }}
              />
              <IconButton
                size="small"
                onClick={() => removePair(idx)}
                disabled={isPredefined}
                sx={{ mt: 0.5 }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        })}
      </Box>

      <Controller
        name="output_field"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            fullWidth
            size="small"
            label="Output field key"
            placeholder="es. eta_calcolata"
            error={!!errors.output_field}
            helperText={errors.output_field?.message}
          />
        )}
      />

      <Controller
        name="next_step_id"
        control={control}
        render={({ field }) => (
          <FormControl fullWidth size="small">
            <InputLabel>Step successivo (opzionale)</InputLabel>
            <Select
              {...field}
              label="Step successivo (opzionale)"
              value={field.value ?? ""}
            >
              <MenuItem value="">— nessuno —</MenuItem>
              {steps
                .filter((s) => s.id !== step.id)
                .map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    [{s.type}] {s.id.slice(0, 8)}… (ord. {s.order})
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        )}
      />

      <Box>
        <Button variant="contained" type="submit" size="small">
          Salva step
        </Button>
      </Box>
    </form>
  );
};
